"use client";

import { useId, type ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { vibrate } from "@/utils/vibrate";

type SortableValue = { id: string };

export function reorderById<T extends SortableValue>(
  items: T[],
  activeId: string,
  overId: string,
): T[] {
  const from = items.findIndex((item) => item.id === activeId);
  const to = items.findIndex((item) => item.id === overId);
  if (from < 0 || to < 0 || from === to) return items;
  return arrayMove(items, from, to);
}

export default function SortableList<T extends SortableValue>({
  items,
  onReorder,
  renderItem,
  disabled = false,
  className,
  itemClassName,
  onItemClick,
}: {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number, handle: ReactNode) => ReactNode;
  disabled?: boolean;
  className?: string;
  itemClassName?: string | ((item: T) => string);
  onItemClick?: (item: T) => void;
}) {
  const id = useId();
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 7 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 7 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const commit = (activeId: string, overId: string) => {
    if (disabled) return;
    const next = reorderById(items, activeId, overId);
    if (next !== items) onReorder(next);
  };
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over) commit(String(active.id), String(over.id));
  };
  return (
    <DndContext
      id={id}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={() => vibrate({ duration: 20 })}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div role="list" className={className}>
          {items.map((item, index) => (
            <SortableRow
              key={item.id}
              id={item.id}
              index={index}
              count={items.length}
              disabled={disabled}
              className={
                typeof itemClassName === "function"
                  ? itemClassName(item)
                  : itemClassName
              }
              onClick={() => onItemClick?.(item)}
              onMove={(direction) => {
                const target = items[index + direction];
                if (target) commit(item.id, target.id);
              }}
            >
              {(handle) => renderItem(item, index, handle)}
            </SortableRow>
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  id,
  index,
  count,
  disabled,
  className,
  onClick,
  onMove,
  children,
}: {
  id: string;
  index: number;
  count: number;
  disabled: boolean;
  className?: string;
  onClick: () => void;
  onMove: (direction: -1 | 1) => void;
  children: (handle: ReactNode) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: disabled || count < 2 });
  const handle = (
    <button
      ref={setActivatorNodeRef}
      type="button"
      {...attributes}
      {...listeners}
      aria-label={`Reorder ${index + 1} of ${count}. Use arrow keys to move.`}
      title="Drag to reorder, or use ArrowUp/ArrowDown"
      disabled={disabled || count < 2}
      className="flex h-11 w-11 shrink-0 cursor-grab touch-none flex-col items-center justify-center gap-1 rounded-lg select-none hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:outline-none active:cursor-grabbing disabled:cursor-default disabled:opacity-50"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (
          !isDragging &&
          (event.key === "ArrowUp" || event.key === "ArrowDown")
        ) {
          event.preventDefault();
          event.stopPropagation();
          onMove(event.key === "ArrowUp" ? -1 : 1);
        } else {
          listeners?.onKeyDown?.(event);
        }
      }}
    >
      <span className="bg-slateCustom h-0.5 w-4 rounded-md" />
      <span className="bg-slateCustom h-0.5 w-4 rounded-md" />
    </button>
  );
  return (
    <div
      ref={setNodeRef}
      role="listitem"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        position: "relative",
        zIndex: isDragging ? 10 : undefined,
      }}
      className={className}
      onClick={onClick}
    >
      {children(handle)}
    </div>
  );
}
